import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import type { components } from '../../api/schema';
import { Select } from '../../components/Select';

export type FormField = components['schemas']['FormFieldModel'];

/**
 * Der Feld-Designer eines Anfrageformulars -- F4-20, K-105.
 *
 * **Der Schluessel wird aus der Beschriftung gebildet und nicht eingegeben.**
 * Wer ein Formular baut, denkt in Fragen und nicht in Bezeichnern -- und ein
 * Bezeichner, den man eingeben muss, wird beim dritten Feld "feld3".
 *
 * **Ein bestehender Schluessel wird nicht nachgezogen**, wenn sich die
 * Beschriftung aendert. Er steht in den Antworten, die schon eingegangen
 * sind; ihn zu aendern hiesse, sie unzuordenbar zu machen.
 */
const TYPES: FormField['type'][] = ['text', 'textarea', 'number', 'date', 'select', 'checkbox'];

export function FormFieldEditor({
  fields,
  onChange,
}: {
  fields: FormField[];
  onChange: (fields: FormField[]) => void;
}) {
  const { t } = useTranslation();

  const [label, setLabel] = useState('');
  const [type, setType] = useState<FormField['type']>('text');
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState('');

  const add = () => {
    const key = toKey(label, fields);
    if (!key) return;
    onChange([
      ...fields,
      {
        key,
        label: label.trim(),
        type,
        required,
        options:
          type === 'select'
            ? options
                .split(',')
                .map((option) => option.trim())
                .filter(Boolean)
            : [],
      },
    ]);
    setLabel('');
    setOptions('');
    setRequired(false);
  };

  return (
    <section>
      <h3>{t('webforms.fields')}</h3>
      <p className="table__muted">{t('webforms.fieldsHint')}</p>

      {fields.length > 0 ? (
        <ul className="task-list">
          {fields.map((field, index) => (
            <li key={field.key} className="task-list__item">
              <span>
                <strong>{field.label}</strong>{' '}
                <span className="table__muted">
                  {t(`formFieldType.${field.type}`)}
                  {field.required ? ` · ${t('webforms.required')}` : ''}
                  {field.options && field.options.length > 0
                    ? ` · ${field.options.join(', ')}`
                    : ''}
                </span>
              </span>
              <Button
                variant="ghost"
                onClick={() => {
                  onChange(fields.filter((_, position) => position !== index));
                }}
              >
                {t('masterdata.delete')}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="field">
        <label className="field__label" htmlFor="field-label">
          {t('webforms.fieldLabel')}
        </label>
        <input
          id="field-label"
          className="field__control"
          value={label}
          placeholder={t('webforms.fieldLabelPlaceholder')}
          onChange={(event) => {
            setLabel(event.target.value);
          }}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="field-type">
          {t('webforms.fieldType')}
        </label>
        <Select
          id="field-type"
          className="field__control"
          value={type}
          onChange={(event) => {
            setType(event.target.value as FormField['type']);
          }}
        >
          {TYPES.map((value) => (
            <option key={value} value={value}>
              {t(`formFieldType.${value}`)}
            </option>
          ))}
        </Select>
      </div>

      {type === 'select' ? (
        <div className="field">
          <label className="field__label" htmlFor="field-options">
            {t('webforms.fieldOptions')}
          </label>
          <input
            id="field-options"
            className="field__control"
            value={options}
            placeholder={t('webforms.fieldOptionsPlaceholder')}
            onChange={(event) => {
              setOptions(event.target.value);
            }}
          />
        </div>
      ) : null}

      <label className="field__checkbox">
        <input
          type="checkbox"
          checked={required}
          onChange={(event) => {
            setRequired(event.target.checked);
          }}
        />
        {t('webforms.required')}
      </label>

      <Button
        variant="secondary"
        disabled={!label.trim() || (type === 'select' && !options.trim())}
        onClick={add}
      >
        {t('webforms.addField')}
      </Button>
    </section>
  );
}

/**
 * Bildet aus einer Beschriftung einen Schluessel.
 *
 * Dieselbe Umschrift wie beim Wissensartikel: Umlaute werden ausgeschrieben,
 * damit aus "Bürostandort" nicht "brostandort" wird. Bei einer Kollision wird
 * gezaehlt -- der Server weist zwei gleiche Schluessel ohnehin ab.
 */
function toKey(label: string, existing: FormField[]): string {
  const replacements: Record<string, string> = {
    ä: 'ae',
    ö: 'oe',
    ü: 'ue',
    Ä: 'ae',
    Ö: 'oe',
    Ü: 'ue',
    ß: 'ss',
  };
  // Zeichenweise ersetzen ueber einen regulaeren Ausdruck statt ueber das
  // Zerlegen der Zeichenkette: Ein Zerlegen zerschnitte Zeichen, die aus
  // mehreren Codeeinheiten bestehen -- fuer eine Umschrift von Umlauten ein
  // unnoetiges Risiko.
  const normalised = label
    .trim()
    .replace(/[äöüÄÖÜß]/g, (char) => replacements[char] ?? char)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

  const base = /^[a-z]/.test(normalised) ? normalised : `feld_${normalised}`.slice(0, 40);
  if (!base) return '';

  const taken = new Set(existing.map((field) => field.key));
  if (!taken.has(base)) return base;
  for (let suffix = 2; suffix < 100; suffix += 1) {
    const candidate = `${base}_${String(suffix)}`.slice(0, 40);
    if (!taken.has(candidate)) return candidate;
  }
  return '';
}
